import { reactive } from '@rehaje/reactive'
import { getPrototypeOf, objectPrototype, values } from '@rehaje/shared'

import { FromSnapshotEvent } from '../events'
import { TypedNode } from '../models'

import type { SnapshotNode } from '../plugins'
import type {
  DependencySnapshot,
  FormSnapshotOptions,
  ImportedSnapshot,
  InboundReferenceReport,
  OutboundReferenceReport,
  ReferenceNode,
  ReferenceReport,
  SnapshotFilterType,
  SnapshotRelation,
  TypedSpecific
} from '../types'
import type { Store } from './Store'

/**
 * 依赖管理中心
 * 所有依赖都必须是实体节点，非实体节点不考虑
 */
export class DependencyStore {
  @reactive('computed')
  get nodes() {
    return this.store.node.__r_nodes
  }

  constructor(public store: Store) {}

  __r_getDeepDependencies(
    nodes: TypedNode[],
    filter?: (node: TypedNode) => boolean
  ): TypedNode[] {
    const result: TypedNode[] = []
    const visited: TypedNode[] = []
    const queue = nodes.slice()
    while (queue.length > 0) {
      const node = queue.shift()
      if (!node || visited.includes(node) || !node.isEntityNode()) continue
      if (filter?.(node) === false) continue
      visited.push(node)
      node.traverse((node: TypedNode) => {
        for (const dep of node.getDependencies()) {
          const depNode = dep.node
          if (!depNode) continue
          if (!visited.includes(depNode)) queue.push(depNode)
          if (!result.includes(depNode)) result.push(depNode)
        }
      })
    }
    return this.__r_getTopCommonNodes(result).filter((item) => {
      return !nodes.some((node) => node.contains(item))
    })
  }

  __r_getTopCommonNodes(
    nodes: TypedNode[],
    filter?: (node: TypedNode) => boolean
  ): TypedNode[] {
    let result: TypedNode[] = []
    for (const node of nodes) {
      if (!node || !node.isEntityNode() || filter?.(node) === false) continue
      if (result.some((source) => source.contains(node))) continue
      if (result.some((source) => node.contains(source)))
        result = result.filter((source) => !node.contains(source))
      result.push(node)
    }
    return result
  }

  __r_toSnapshot(
    nodes: TypedNode[],
    filter?: (node: TypedNode, type: SnapshotFilterType) => boolean
  ): DependencySnapshot {
    const sourceNodes = this.__r_getTopCommonNodes(nodes, (node) => {
      return filter?.(node, 'Source') !== false
    })
    const dependencyNodes = this.__r_getDeepDependencies(sourceNodes, (node) => {
      return filter?.(node, 'Dependency') !== false
    })

    const snapshotNodes: DependencySnapshot['nodes'] = {}
    const snapshotSources: DependencySnapshot['sources'] = []
    const snapshotDifferences: DependencySnapshot['differences'] = []
    const snapshotDependencies: DependencySnapshot['dependencies'] = []

    for (const source of sourceNodes) {
      const sourceUid = source.__r_unique_id
      snapshotNodes[sourceUid] = source.toSnapshot()
      snapshotSources.push(sourceUid)
      if (dependencyNodes.some((dependency) => dependency.contains(source)))
        continue
      snapshotDifferences.push(sourceUid)
    }
    for (const dependency of dependencyNodes) {
      const dependencyUid = dependency.__r_unique_id
      snapshotNodes[dependencyUid] = dependency.toSnapshot()
      snapshotDependencies.push(dependencyUid)
    }

    return {
      dependencies: snapshotDependencies,
      differences: snapshotDifferences,
      nodes: snapshotNodes,
      sources: snapshotSources
    }
  }

  /**
   * 导入快照，支持是否携带依赖导入
   */
  @reactive('batch')
  fromSnapshot(
    snapshot: DependencySnapshot,
    options?: FormSnapshotOptions
  ): ImportedSnapshot {
    const rootNode = this.store.node
    const relation: SnapshotRelation = {}
    const imported: ImportedSnapshot = {
      dependencies: [],
      nodes: [],
      sources: []
    }
    const resolver =
      options?.resolver ?? ((node, root) => node.fromSnapshot(root))
    const importIds = options?.withDependencies
      ? snapshot.differences.concat(snapshot.dependencies)
      : snapshot.sources
    const sortedSpecifics = importIds
      .map((id) => snapshot.nodes[id])
      .filter(Boolean)
      .toSorted((a, b) => a.__r_order - b.__r_order)
    let previousNode: TypedNode | undefined
    for (const specific of sortedSpecifics) {
      const node = this.store.createTypedNode(specific) as TypedNode
      /**
       * 用户必须重载定制 fromSnapshot 方法，否则不知道如何处理插入逻辑
       * 在 fromSnapshot 中预期将这个新节点成功插入到主树上，并且处理好 id 自增
       */
      resolver(node, rootNode, previousNode)
      previousNode = node
      if (rootNode.isInSameTree(node)) {
        if (snapshot.dependencies.includes(specific.__r_snapshot_id)) {
          imported.dependencies.push(node)
        } else imported.sources.push(node)
        /**
         * 插入后把内部所有的子孙节点都添加到 snapshotID-node 映射表中
         * 后续自动重命名逻辑会根据这个映射表来重命名子孙节点
         */
        node.traverse((child) => {
          relation[child.getSnapshotID()] = child
        })
      }
    }
    for (const node of values(relation)) {
      node.traverse((child) => {
        imported.nodes.push(child)
        child.dispatchEvent(new FromSnapshotEvent(relation))
      })
    }
    for (const node of imported.nodes) {
      node.__r_setSnapshotID(node.getUniqueID())
    }
    return imported
  }

  /**
   * 获取一批节点的所有外部依赖节点（处理子孙节点的依赖）：自己及子孙节点依赖了谁
   * 注意返回的集合会取顶层公共节点集合
   */
  @reactive('batch')
  getDependencyNodes(
    nodes: TypedNode[],
    filter?: (node: TypedNode) => boolean
  ): TypedNode[] {
    const result: TypedNode[] = []
    for (const node of nodes) {
      if (!node || !node.isEntityNode()) continue
      node.traverse((child: TypedNode) => {
        for (const dep of child.getDependencies()) {
          const depNode = dep.node
          if (!depNode || nodes.includes(depNode) || result.includes(depNode))
            continue
          if (!depNode.isEntityNode() || filter?.(depNode) === false) continue
          if (nodes.some((node) => node.contains(depNode))) continue
          result.push(depNode)
        }
      })
    }
    return this.__r_getTopCommonNodes(result)
  }

  /**
   * 获取一批节点的所有被依赖的节点（处理依赖子孙的节点）：谁依赖了自己或子孙节点
   * 注意返回的集合不会取顶层公共节点集合
   */
  @reactive('batch')
  getDependentNodes(
    nodes: TypedNode[],
    filter?: (node: TypedNode) => boolean
  ): TypedNode[] {
    const sources: TypedNode[] = []
    for (const node of nodes) {
      node?.traverse((child) => {
        if (sources.includes(child)) return false
        if (child.isEntityNode()) sources.push(child)
      })
    }
    const result: TypedNode[] = []
    for (const node of this.nodes) {
      if (!node || sources.includes(node) || !node.isEntityNode()) continue
      if (filter?.(node) === false) continue
      node.traverse((child) => {
        if (result.includes(node)) return false
        if (child !== node && child.isEntityNode()) return false
        for (const dep of child.getDependencies()) {
          if (sources.includes(dep.node)) {
            result.push(node)
            return false
          }
        }
      })
    }
    return result
  }

  /**
   * 计算单个目标节点的入向引用报告（谁依赖了我 / 谁调用了我）
   */
  @reactive('batch')
  getInboundReport(node?: TypedNode): InboundReferenceReport {
    const calledBy: ReferenceNode[] = []
    const referencedBy: ReferenceNode[] = []
    if (node?.isEntityNode()) {
      const uniqueMap = new Map<string, ReferenceNode>()
      for (const sourceNode of this.nodes) {
        if (!sourceNode?.isEntityNode()) continue
        sourceNode.__r_traverseSlaveNode((slaveNode) => {
          const locationPath = slaveNode.__r_location.map(String)
          for (const dep of slaveNode.getDependencies()) {
            const depNode = dep.node
            if (!depNode?.is(node)) continue
            const sourceId = sourceNode.id
            const targetId = node.id
            const relation = dep.relation ?? 'DEPENDENCY'
            const key = `${sourceId}:${targetId}:${locationPath}:${relation}`
            if (uniqueMap.has(key)) continue
            uniqueMap.set(key, {
              locationPath,
              memberChain: dep.invokedMethod
                ? dep.path.map(String).concat(dep.invokedMethod)
                : dep.path.map(String),
              relation,
              sourceId,
              sourceType: sourceNode.type,
              targetId,
              targetType: node.type
            })
          }
        })
      }
      for (const ref of uniqueMap.values()) {
        if (ref.relation === 'INVOCATION') {
          calledBy.push(ref)
        } else {
          referencedBy.push(ref)
        }
      }
    }
    return { calledBy, referencedBy }
  }

  /**
   * 计算单个目标节点的出向引用报告（我依赖了谁 / 我调用了谁）
   */
  @reactive('batch')
  getOutboundReport(node?: TypedNode): OutboundReferenceReport {
    const calls: ReferenceNode[] = []
    const references: ReferenceNode[] = []
    if (node?.isEntityNode()) {
      const uniqueMap = new Map<string, ReferenceNode>()
      node.__r_traverseSlaveNode((slaveNode) => {
        const locationPath = slaveNode.__r_location.map(String)
        for (const dep of slaveNode.getDependencies()) {
          const depNode = dep.node
          if (!depNode?.isEntityNode()) continue
          const sourceId = node.id
          const targetId = depNode.id
          const relation = dep.relation ?? 'DEPENDENCY'
          const key = `${sourceId}:${targetId}:${locationPath}:${relation}`
          if (uniqueMap.has(key)) continue
          uniqueMap.set(key, {
            locationPath,
            memberChain: dep.invokedMethod
              ? dep.path.map(String).concat(dep.invokedMethod)
              : dep.path.map(String),
            relation,
            sourceId,
            sourceType: node.type,
            targetId,
            targetType: depNode.type
          })
        }
      })
      for (const ref of uniqueMap.values()) {
        if (ref.relation === 'INVOCATION') {
          calls.push(ref)
        } else {
          references.push(ref)
        }
      }
    }
    return { calls, references }
  }

  /**
   * 计算单个目标节点的四象限引用报告
   *
   * - 入向（谁用我 / 我所在子树）：referencedBy / calledBy
   * - 出向（我用谁 / 我所在子树）：references / calls
   */
  @reactive('batch')
  getReferenceReport(node?: TypedNode): ReferenceReport {
    return {
      ...this.getInboundReport(node),
      ...this.getOutboundReport(node)
    }
  }

  /**
   * 获取一批节点与节点关系快照
   *
   * 所有依赖都必须是考虑直接依赖和间接依赖，即全部依赖，所有依赖都必须是实体节点，非实体节点不考虑
   * 另外允许传入携带 id 的 TypedSpecific 进行预建模分析，注意不要传入混合的协议和真实节点
   */
  @reactive('batch')
  toSnapshot(
    payload: TypedSpecific[],
    filter?: (node: TypedNode, type: SnapshotFilterType) => boolean
  ): DependencySnapshot {
    let snapshot: SnapshotNode | undefined
    const nodes: TypedNode[] = []
    const specifics: TypedSpecific[] = []
    for (const item of payload) {
      if (!item) continue
      else if (item instanceof TypedNode) nodes.push(item)
      else if (getPrototypeOf(item) === objectPrototype) specifics.push(item)
    }
    if (specifics.length > 0) {
      snapshot = this.store.createTypedNode({
        children: specifics,
        type: 'Snapshot'
      })
      for (const node of snapshot.children) {
        if (node instanceof TypedNode) nodes.push(node)
      }
    }
    const result = this.__r_toSnapshot(nodes, filter)
    snapshot?.dispose(true, true)
    return result
  }
}

/**
 * TODO: 这里的 batch 都应该改成 action，但是为了解决下面问题暂时改成了 batch
 * https://meego.larkoffice.com/apaas_/issue/detail/5412620566
 *
 * 实际上是因为 action 导致了 isActiveSignal 函数为 false，无法创建响应源，导致自动建模失效
 * 最佳解法是提供一个新的 optimize 包装器，专门用于初始化 store 阶段的性能优化，不要影响正常的 action
 */
